import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useUsersHook } from "../useUsersHook";
import { userActionSet } from "../../redux/actions/userActions";

// ─── Storage Keys ────────────────────────────────────────────────────────────
// All localStorage keys managed by this hook, mapped to their user profile path
const SYNCED_KEYS = {
  "lo-pipeline-layouts": {
    profileKey: "pipelineLayouts",
    parse: true,
    scope: "pipeline",
  },
  "lo-pipeline-default-layout": {
    profileKey: "pipelineDefaultLayout",
    parse: false,
    scope: "pipeline",
  },
  "lo-pipeline-assigned-to-me": {
    profileKey: "pipelineAssignedToMe",
    parse: false,
    scope: "pipeline",
  },
  "lo-pipeline-favorite-loans": {
    profileKey: "pipelineFavoriteLoans",
    parse: true,
    scope: "pipeline",
  },
  "lo-pipeline-show-favorites": {
    profileKey: "pipelineShowFavorites",
    parse: false,
    scope: "pipeline",
  },
};
const PROFILE_PREFS_KEY = "preferences";

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Returns true if a value from the profile or localStorage is a garbage/sentinel
 * value that should be treated as "not set". Guards against Python's None being
 * serialized as the literal string "None", as well as other bad values.
 */
const isGarbageValue = (val) =>
  val === null ||
  val === undefined ||
  val === "None" ||
  val === "null" ||
  val === "undefined" ||
  val === "";

/**
 * Validates that a profile value has the correct type for its storage key.
 * - parse: true keys (arrays) must be actual arrays
 * - parse: false keys (strings) must be non-garbage strings
 * Returns the sanitized value or null if invalid.
 */
const sanitizeProfileValue = (value, parse) => {
  if (isGarbageValue(value)) return null;

  if (parse) {
    // parse:true keys expect arrays (pipelineLayouts, pipelineFavoriteLoans)
    return Array.isArray(value) ? value : null;
  }

  // parse:false keys expect simple strings ("true", "false", layout IDs)
  return typeof value === "string" ? value : null;
};

/**
 * Read all synced localStorage keys into a flat object keyed by profileKey.
 * All values are validated for correct type before being returned.
 */
const readAllLocalStoragePrefs = () => {
  const pipeline = {};
  const root = {};

  for (const [storageKey, { profileKey, parse, scope }] of Object.entries(
    SYNCED_KEYS,
  )) {
    try {
      const raw = localStorage.getItem(storageKey);
      if (isGarbageValue(raw)) {
        (scope === "root" ? root : pipeline)[profileKey] = null;
        continue;
      }
      if (parse) {
        const parsed = JSON.parse(raw);
        (scope === "root" ? root : pipeline)[profileKey] = Array.isArray(parsed)
          ? parsed
          : null;
      } else {
        (scope === "root" ? root : pipeline)[profileKey] = raw;
      }
    } catch {
      (scope === "root" ? root : pipeline)[profileKey] = null;
    }
  }
  return { pipeline, root };
};

/**
 * Write profile preferences back into localStorage (used on hydration).
 * Values are validated before writing to prevent corrupted data.
 */
const writeProfilePrefsToLocalStorage = (prefs) => {
  if (!prefs) return;
  for (const [storageKey, { profileKey, parse, scope }] of Object.entries(
    SYNCED_KEYS,
  )) {
    const source = scope === "root" ? prefs : prefs?.pipeline;
    const value = source?.[profileKey];
    const sanitized = sanitizeProfileValue(value, parse);
    if (sanitized !== null) {
      try {
        localStorage.setItem(
          storageKey,
          parse ? JSON.stringify(sanitized) : String(sanitized),
        );
      } catch {
        // storage full or restricted
      }
    }
  }
};

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * useUserPreferences
 *
 * Keeps localStorage pipeline preferences in sync with the user profile.
 *
 * Hydration (profile → localStorage):
 *   On mount, if the user profile has saved preferences but localStorage is
 *   missing data, it writes the profile values into localStorage so a user
 *   logging in on a new device gets their settings immediately.
 *
 * Sync (localStorage → profile):
 *   Call `syncPreferences()` after any localStorage write. It debounces and
 *   batches all managed keys into a single profile update.
 *
 * Usage in LoanOfficerPipelineScreen:
 *   const { syncPreferences } = useUserPreferences();
 *   // After any localStorage write:
 *   persistLayouts(updated);
 *   syncPreferences();
 */
export const useUserPreferences = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state?.user);
  const { updateUserById } = useUsersHook();

  const userRef = useRef(user);
  const syncTimerRef = useRef(null);
  const hydratedRef = useRef(false);

  // Keep userRef current so the debounced sync always uses latest user
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // ── Hydration: profile → localStorage (runs once when user loads) ──
  // ── Hydration: profile → localStorage (runs once when user loads) ──
  useEffect(() => {
    if (hydratedRef.current || !user?._id) return;
    hydratedRef.current = true;

    const profilePrefs = user?.[PROFILE_PREFS_KEY];
    if (!profilePrefs) return;

    let didHydrate = false;
    for (const [storageKey, { profileKey, parse, scope }] of Object.entries(
      SYNCED_KEYS,
    )) {
      const localValue = localStorage.getItem(storageKey);
      const source = scope === "root" ? profilePrefs : profilePrefs?.pipeline;
      const profileValue = source?.[profileKey];
      const sanitized = sanitizeProfileValue(profileValue, parse);

      if (localValue === null && sanitized !== null) {
        try {
          localStorage.setItem(
            storageKey,
            parse ? JSON.stringify(sanitized) : String(sanitized),
          );
          didHydrate = true;
        } catch {
          // skip
        }
      }
    }

    if (didHydrate) {
      window.dispatchEvent(new Event("preferences-hydrated"));
    }
  }, [user]);

  // ── Sync: localStorage → profile (debounced) ──
  const syncPreferences = useCallback(() => {
    if (syncTimerRef.current) {
      clearTimeout(syncTimerRef.current);
    }

    syncTimerRef.current = setTimeout(() => {
      const currentUser = userRef.current;
      if (!currentUser?._id) return;

      const { pipeline: pipelinePrefs, root: rootPrefs } =
        readAllLocalStoragePrefs();
      const existingPrefs = currentUser?.[PROFILE_PREFS_KEY] || {};

      updateUserById({
        userId: currentUser._id,
        data: {
          [PROFILE_PREFS_KEY]: {
            ...existingPrefs,
            // Root-level keys as siblings to pipeline
            ...rootPrefs,
            pipeline: {
              ...existingPrefs?.pipeline,
              ...pipelinePrefs,
              updatedAt: new Date().toISOString(),
            },
          },
        },
        onSuccessFn: (updatedUser) => {
          dispatch(userActionSet(updatedUser));
        },
        onErrorFn: (err) => {
          console.error("Failed to sync preferences to profile:", err);
        },
      });
    }, 1500);
  }, [updateUserById, dispatch]);
  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (syncTimerRef.current) {
        clearTimeout(syncTimerRef.current);
      }
    };
  }, []);

  return { syncPreferences };
};
